import React, { useCallback, useMemo } from 'react';

import type { ControlGroupPropsBase } from '@splunk/react-ui/ControlGroup';
import type {
    RadioListPropsBaseControlled,
    RadioListChangeHandler,
} from '@splunk/react-ui/RadioList';
import type { Expand } from '@splunk/dashboard-types';

import { ControlGroup } from '../ControlGroup';
import { buildOptionRows } from './optionRows';
import {
    RadioControlGroupOption,
    type RadioControlGroupValue,
} from './RadioControlGroupOption';
import { RadioInputGrid, StyledRadioList } from './styles';

export type RadioChangeEvent = Parameters<RadioListChangeHandler>[0];
type RadioOnChangePayload = Parameters<RadioListChangeHandler>[1];

export type OnChangePayload = Expand<
    { value: RadioControlGroupValue } & Omit<RadioOnChangePayload, 'value'>
>;

export type SelectiveControlChangeHandler = (
    event: RadioChangeEvent,
    data: OnChangePayload
) => void;

type ControlGroupProps = Pick<
    ControlGroupPropsBase,
    | 'error'
    | 'help'
    | 'hideLabel'
    | 'label'
    | 'required'
    | 'tooltip'
    | 'tooltipDefaultPlacement'
>;

type RadioListProps = Pick<
    RadioListPropsBaseControlled,
    'defaultValue' | 'disabled' | 'name'
>;

export type RadioControlGroupProps = ControlGroupProps &
    RadioListProps & {
        onChange: SelectiveControlChangeHandler;
        value: RadioControlGroupValue;
        children?: React.ReactNode;
        maxLabelWidth?: string | number;
        'data-test'?: string;
    };

export const RadioControlGroup = ({
    defaultValue,
    disabled,
    name,
    onChange,
    value,
    children,
    maxLabelWidth,
    'data-test': dataTest = 'exclusive-control-group',
    ...controlGroupProps
}: RadioControlGroupProps) => {
    const [radioOptions, nestedControls] = useMemo(
        () => buildOptionRows({ children, disabled, selectedValue: value }),
        [children, value, disabled]
    );

    const handleChange = useCallback<RadioListChangeHandler>(
        (event, { value: updatedValue, ...payload }) => {
            onChange?.(event, {
                value: updatedValue as RadioControlGroupValue,
                ...payload,
            });
        },
        [onChange]
    );

    return (
        <RadioInputGrid $maxLabelWidth={maxLabelWidth} data-test={dataTest}>
            <ControlGroup controlsLayout="none" {...controlGroupProps}>
                <StyledRadioList
                    defaultValue={defaultValue}
                    disabled={disabled}
                    aria-label={controlGroupProps.label}
                    name={name}
                    onChange={handleChange}
                    value={value}
                >
                    {radioOptions}
                </StyledRadioList>
            </ControlGroup>
            {nestedControls}
        </RadioInputGrid>
    );
};

RadioControlGroup.Option = RadioControlGroupOption;
